import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../Navbar/Navbar';
import '../../styles/Blogs/Riskmanagementblog.css';

export default function RiskManagementBlog() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [visibleSections, setVisibleSections] = useState({});
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [activeSection, setActiveSection] = useState('');
  const [isHeroLoaded, setIsHeroLoaded] = useState(false);
  const heroRef = useRef(null);
  const articleRef = useRef(null);

  // Scroll progress bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hero entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setIsHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const sections = document.querySelectorAll('.rpg-anim');
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Active section tracking for sidebar
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-100px 0px -60% 0px' }
    );

    const sections = ['section-1', 'section-2', 'section-3', 'section-4', 'section-5'];
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Hero parallax mouse effect
  useEffect(() => {
    const handleMouse = (e) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMousePos({
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
        y: ((e.clientY - rect.top) / rect.height - 0.5) * 10,
      });
    };
    const hero = heroRef.current;
    if (hero) hero.addEventListener('mousemove', handleMouse);
    return () => { if (hero) hero.removeEventListener('mousemove', handleMouse); };
  }, []);

  // Smooth scroll to section
  const scrollToSection = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  const vis = (id) => visibleSections[id] ? 'rpg-anim rpg-anim--visible' : 'rpg-anim';

  const sidebarItems = [
    { id: 'section-1', label: 'World Has Changed' },
    { id: 'section-2', label: 'Common Misconceptions' },
    { id: 'section-3', label: 'Smarter Approach' },
    { id: 'section-4', label: 'Real-World Angle' },
    { id: 'section-5', label: 'What This Means' },
  ];

  return (
    <>
      <Navbar />

      {/* Progress bar */}
      <div 
        className="rpg-progress" 
        style={{ transform: `scaleX(${scrollProgress / 100})` }} 
        aria-hidden="true" 
      />

      <main className="rpg-page">

        {/* ── Breadcrumb ── */}
        <nav className={`rpg-breadcrumb ${isHeroLoaded ? 'rpg-breadcrumb--visible' : ''}`}>
          <Link to="/blogs" className="rpg-back">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>All Blogs</span>
          </Link>
        </nav>

        {/* ── HERO ── */}
        <header 
          className={`rpg-hero ${isHeroLoaded ? 'rpg-hero--loaded' : ''}`} 
          ref={heroRef}
        >
          {/* Background layers */}
          <div className="rpg-hero__bg" aria-hidden="true">
            {/* Subtle noise texture overlay */}
            <div className="rpg-hero__noise" />

            {/* Animated grid with depth */}
            <svg className="rpg-hero__grid" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice">
              <defs>
                <linearGradient id="g1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#0d9488" stopOpacity="0.15"/>
                  <stop offset="50%" stopColor="#14b8a6" stopOpacity="0.08"/>
                  <stop offset="100%" stopColor="#5eead4" stopOpacity="0.03"/>
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              {/* Vertical lines with varying opacity */}
              {[...Array(24)].map((_, i) => (
                <line 
                  key={`v${i}`} 
                  x1={i * 52} 
                  y1="0" 
                  x2={i * 52} 
                  y2="600" 
                  stroke="#0d9488" 
                  strokeWidth="0.5" 
                  strokeOpacity={0.04 + (i % 3) * 0.02}
                />
              ))}

              {/* Horizontal lines */}
              {[...Array(12)].map((_, i) => (
                <line 
                  key={`h${i}`} 
                  x1="0" 
                  y1={i * 52} 
                  x2="1200" 
                  y2={i * 52} 
                  stroke="#0d9488" 
                  strokeWidth="0.5" 
                  strokeOpacity={0.03 + (i % 4) * 0.015}
                />
              ))}

              {/* Flowing data curves */}
              <path 
                d="M0 380 Q200 280 400 320 T800 260 T1200 180" 
                stroke="#14b8a6" 
                strokeWidth="2" 
                fill="none" 
                strokeOpacity="0.25" 
                strokeDasharray="10 6"
                filter="url(#glow)"
              />
              <path 
                d="M0 420 Q300 300 600 360 T1200 240" 
                stroke="#0d9488" 
                strokeWidth="1.5" 
                fill="none" 
                strokeOpacity="0.15"
              />
              <path 
                d="M0 460 Q400 340 800 400 T1200 300" 
                stroke="#5eead4" 
                strokeWidth="1" 
                fill="none" 
                strokeOpacity="0.12"
              />

              {/* Ambient orbs */}
              <circle cx="600" cy="300" r="220" fill="url(#g1)" opacity="0.6"/>
              <circle cx="180" cy="120" r="100" fill="#14b8a6" fillOpacity="0.04"/>
              <circle cx="1050" cy="480" r="140" fill="#0d9488" fillOpacity="0.05"/>
              <circle cx="900" cy="100" r="60" fill="#5eead4" fillOpacity="0.06"/>
            </svg>

            {/* Parallax orb */}
            <div
              className="rpg-hero__orb"
              style={{ transform: `translate(${mousePos.x}px, ${mousePos.y}px)` }}
            />

            {/* Gradient fade at bottom */}
            <div className="rpg-hero__fade" />
          </div>

          <div className="rpg-hero__content">
            <div className="rpg-hero__meta">
              <span className="rpg-tag">Industry Insights</span>
              <span className="rpg-dot" aria-hidden="true"/>
              <span className="rpg-readtime">5 min read</span>
            </div>

            <h1 className="rpg-hero__title">
              <span className="rpg-hero__title-line">Why Most Businesses</span>
              <span className="rpg-hero__title-line rpg-hero__title-line--accent">Are Leaving Money</span>
              <span className="rpg-hero__title-line">on the Table</span>
            </h1>

            <p className="rpg-hero__sub">with Poor Risk Management</p>

            <div className="rpg-hero__byline">
              <div className="rpg-avatar">
                <span>A</span>
              </div>
              <div className="rpg-hero__author-info">
                <span className="rpg-author">Actyra Advisory Team</span>
                <span className="rpg-date">June 2026</span>
              </div>
            </div>
          </div>

          {/* Floating stat cards */}
          <div className="rpg-hero__stats" aria-hidden="true">
            <div className="rpg-stat-card rpg-stat-card--1">
              <div className="rpg-stat-card__inner">
                <span className="rpg-stat-num">67%</span>
                <span className="rpg-stat-label">SMEs lack formal risk framework</span>
              </div>
              <div className="rpg-stat-card__glow" />
            </div>
            <div className="rpg-stat-card rpg-stat-card--2">
              <div className="rpg-stat-card__inner">
                <span className="rpg-stat-num">3×</span>
                <span className="rpg-stat-label">Cost of recovery vs prevention</span>
              </div>
              <div className="rpg-stat-card__glow" />
            </div>
          </div>

          {/* Scroll hint */}
          <div className="rpg-hero__scroll-hint" aria-hidden="true">
            <div className="rpg-hero__scroll-line"/>
            <span>Scroll to explore</span>
          </div>
        </header>

        {/* ── Article ── */}
        <div className="rpg-layout" ref={articleRef}>

          {/* Sticky sidebar nav */}
          <aside className="rpg-sidebar" aria-label="Article sections">
            <div className="rpg-sidebar__inner">
              <div className="rpg-sidebar__header">
                <div className="rpg-sidebar__line" aria-hidden="true"/>
                <p className="rpg-sidebar__label">Contents</p>
              </div>
              <nav className="rpg-sidebar__nav">
                {sidebarItems.map((item, index) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => scrollToSection(e, item.id)}
                    className={`rpg-sidebar__link ${activeSection === item.id ? 'rpg-sidebar__link--active' : ''}`}
                  >
                    <span className="rpg-sidebar__num">{String(index + 1).padStart(2, '0')}</span>
                    <span className="rpg-sidebar__pip" aria-hidden="true"/>
                    <span className="rpg-sidebar__text">{item.label}</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <article className="rpg-article">

            {/* Lead paragraph */}
            <div className={`rpg-lead-wrap ${vis('lead')}`} id="lead">
              <div className="rpg-lead-border" aria-hidden="true">
                <div className="rpg-lead-border__dot" />
              </div>
              <p className="rpg-lead">
                Every year, businesses across Africa lose billions to risks they never saw coming — not
                because those risks were unpredictable, but because no one was looking in the right
                direction. In Kenya alone, companies continue to make major financial decisions without a
                clear picture of their exposure. The cost of that blind spot is rarely visible until it's
                too late.
              </p>
            </div>

            {/* ── Section 1 ── */}
            <section className={`rpg-section ${vis('section-1')}`} id="section-1">
              <div className="rpg-section__number" aria-hidden="true">
                <span>01</span>
                <div className="rpg-section__number-line" />
              </div>
              <div className="rpg-section__body">
                <h2 className="rpg-h2">
                  <span className="rpg-h2__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>
                    </svg>
                  </span>
                  The World Has Changed.<br/>
                  <em>Most Business Plans Haven't.</em>
                </h2>
                <p>
                  Risk used to mean fire, theft, or a bad season. Today it means fluctuating exchange
                  rates, regulatory shifts, the sudden exit of a key client, or a compliance gap that
                  surfaces during an audit. The business environment in East Africa is maturing fast —
                  and with that maturity comes complexity that most owners and managers were never trained
                  to navigate.
                </p>
                <p>
                  Here's the reality: the majority of small and mid-sized businesses operate without any
                  formal risk framework. Decisions get made on instinct, on precedent, or on the advice
                  of someone who isn't necessarily qualified to give it. That works — until it doesn't.
                </p>
                <p>
                  What's at stake isn't just profit. It's sustainability. A single unmitigated risk event
                  — a regulatory penalty, an uninsured loss, a poorly structured product — can erase
                  years of growth in a matter of months.
                </p>

                {/* Inline chart visual */}
                <div className="rpg-chart-strip" aria-hidden="true">
                  <div className="rpg-chart-strip__header">
                    <span className="rpg-chart-strip__title">Risk Exposure Over Time</span>
                    <span className="rpg-chart-strip__badge">Projected</span>
                  </div>
                  <svg viewBox="0 0 480 90" fill="none" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.25"/>
                        <stop offset="100%" stopColor="#14b8a6" stopOpacity="0"/>
                      </linearGradient>
                      <linearGradient id="chartLine" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#0d9488"/>
                        <stop offset="100%" stopColor="#5eead4"/>
                      </linearGradient>
                    </defs>
                    <path d="M0 70 Q60 50 120 55 T240 30 T360 40 T480 10 L480 90 L0 90 Z" fill="url(#chartFill)"/>
                    <path d="M0 70 Q60 50 120 55 T240 30 T360 40 T480 10" stroke="url(#chartLine)" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
                    {[0, 120, 240, 360, 480].map((x, i) => {
                      const ys = [70, 55, 30, 40, 10];
                      return (
                        <g key={i}>
                          <circle cx={x} cy={ys[i]} r="5" fill="#ffffff" stroke="#0d9488" strokeWidth="2"/>
                          <circle cx={x} cy={ys[i]} r="2" fill="#0d9488"/>
                        </g>
                      );
                    })}
                  </svg>
                  <div className="rpg-chart-labels">
                    <span>Year 1</span><span>Year 2</span><span>Year 3</span><span>Year 4</span><span>Year 5</span>
                  </div>
                  <p className="rpg-chart-caption">Unmanaged risk compounds over time, accelerating financial exposure</p>
                </div>
              </div>
            </section>

            {/* ── Callout ── */}
            <blockquote className={`rpg-callout ${vis('callout-1')}`} id="callout-1">
              <div className="rpg-callout__mark" aria-hidden="true">"</div>
              <div className="rpg-callout__content">
                <p className="rpg-callout__text">
                  We're too small for that — a phrase that has quietly put more businesses out of
                  operation than any market downturn.
                </p>
                <div className="rpg-callout__line" aria-hidden="true"/>
                <span className="rpg-callout__source">— A common refrain among failing SMEs</span>
              </div>
            </blockquote>

            {/* ── Section 2 ── */}
            <section className={`rpg-section ${vis('section-2')}`} id="section-2">
              <div className="rpg-section__number" aria-hidden="true">
                <span>02</span>
                <div className="rpg-section__number-line" />
              </div>
              <div className="rpg-section__body">
                <h2 className="rpg-h2">
                  <span className="rpg-h2__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </span>
                  What Most People<br/><em>Get Wrong</em>
                </h2>
                <p>
                  The most common misconception is that risk management is something only large
                  corporations need to worry about.
                </p>

                {/* Two-panel comparison */}
                <div className="rpg-compare">
                  <div className="rpg-compare__panel rpg-compare__panel--wrong">
                    <div className="rpg-compare__header">
                      <div className="rpg-compare__icon rpg-compare__icon--wrong">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </div>
                      <span className="rpg-compare__label">Common Belief</span>
                    </div>
                    <h4>Insurance Equals Protection</h4>
                    <p>If something goes wrong, we're covered. That's all the risk management we need.</p>
                  </div>

                  <div className="rpg-compare__divider" aria-hidden="true">
                    <div className="rpg-compare__divider-line" />
                    <div className="rpg-compare__divider-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </div>

                  <div className="rpg-compare__panel rpg-compare__panel--right">
                    <div className="rpg-compare__header">
                      <div className="rpg-compare__icon rpg-compare__icon--right">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                      <span className="rpg-compare__label">The Reality</span>
                    </div>
                    <h4>Prevention Over Reaction</h4>
                    <p>Insurance addresses outcomes after the fact. Risk management prevents exposure before it happens.</p>
                  </div>
                </div>

                <p>
                  Insurance is one tool in a much larger toolkit. Risk
                  management is about understanding your exposure <em>before</em> something happens — so
                  you can price correctly, plan confidently, and protect what you've built.
                </p>
              </div>
            </section>

            {/* ── Section 3 ── */}
            <section className={`rpg-section ${vis('section-3')}`} id="section-3">
              <div className="rpg-section__number" aria-hidden="true">
                <span>03</span>
                <div className="rpg-section__number-line" />
              </div>
              <div className="rpg-section__body">
                <h2 className="rpg-h2">
                  <span className="rpg-h2__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </span>
                  The Smarter<br/><em>Approach</em>
                </h2>
                <p>
                  Effective risk management doesn't require a department or a massive budget. It requires
                  the right thinking applied at the right moments.
                </p>

                <ol className="rpg-steps">
                  {[
                    {
                      num: '01',
                      title: 'Know your exposure',
                      body: "Map out the areas of your business most vulnerable to financial loss, regulatory change, or operational disruption. You can't manage what you haven't identified.",
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                        </svg>
                      )
                    },
                    {
                      num: '02',
                      title: 'Get independent advice',
                      body: 'Internal teams are close to the business — an advantage and a limitation. An external advisory perspective surfaces blind spots that familiarity tends to obscure.',
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      )
                    },
                    {
                      num: '03',
                      title: 'Build it into decision-making',
                      body: "Risk assessment shouldn't be an annual exercise buried in a board report. It should inform how you price a product, structure a contract, or plan for growth.",
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                        </svg>
                      )
                    },
                    {
                      num: '04',
                      title: 'Revisit regularly',
                      body: 'A risk profile from two years ago may bear little resemblance to where your business sits today. Markets shift. Regulations change. Your risk strategy should keep pace.',
                      icon: (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                        </svg>
                      )
                    },
                  ].map((step, index) => (
                    <li key={step.num} className="rpg-step">
                      <div className="rpg-step__left">
                        <div className="rpg-step__num">{step.num}</div>
                        {index < 3 && <div className="rpg-step__connector" aria-hidden="true"/>}
                      </div>
                      <div className="rpg-step__card">
                        <div className="rpg-step__card-accent" aria-hidden="true"/>
                        <div className="rpg-step__icon">{step.icon}</div>
                        <h4 className="rpg-step__title">{step.title}</h4>
                        <p className="rpg-step__body">{step.body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </section>

            {/* ── Section 4 case study ── */}
            <section className={`rpg-section ${vis('section-4')}`} id="section-4">
              <div className="rpg-section__number" aria-hidden="true">
                <span>04</span>
                <div className="rpg-section__number-line" />
              </div>
              <div className="rpg-section__body">
                <h2 className="rpg-h2">
                  <span className="rpg-h2__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                  </span>
                  A Real-World<br/><em>Angle</em>
                </h2>

                <div className="rpg-case">
                  <div className="rpg-case__header">
                    <div className="rpg-case__badge-wrap">
                      <span className="rpg-case__badge">Case Study</span>
                      <span className="rpg-case__badge-line" aria-hidden="true"/>
                    </div>
                    <div className="rpg-case__line" aria-hidden="true"/>
                  </div>
                  <div className="rpg-case__body">
                    <div className="rpg-case__icon-col" aria-hidden="true">
                      <div className="rpg-case__icon-bg">
                        <svg viewBox="0 0 80 120" fill="none">
                          <rect x="10" y="20" width="60" height="80" rx="6" stroke="#0d9488" strokeWidth="1.5" strokeOpacity="0.2"/>
                          <rect x="20" y="35" width="40" height="6" rx="2" fill="#14b8a6" fillOpacity="0.25"/>
                          <rect x="20" y="48" width="30" height="4" rx="2" fill="#0d9488" fillOpacity="0.15"/>
                          <rect x="20" y="58" width="35" height="4" rx="2" fill="#0d9488" fillOpacity="0.15"/>
                          <rect x="20" y="68" width="25" height="4" rx="2" fill="#0d9488" fillOpacity="0.15"/>
                          <path d="M20 85 Q30 78 40 82 T60 76" stroke="#14b8a6" strokeWidth="2" fill="none" strokeOpacity="0.4"/>
                          <circle cx="20" cy="85" r="3" fill="#0d9488" fillOpacity="0.4"/>
                          <circle cx="40" cy="82" r="3" fill="#14b8a6" fillOpacity="0.5"/>
                          <circle cx="60" cy="76" r="3" fill="#5eead4" fillOpacity="0.6"/>
                        </svg>
                      </div>
                    </div>
                    <div className="rpg-case__text-col">
                      <p>
                        Consider a mid-sized logistics company that expanded its fleet and client base rapidly
                        over eighteen months. On paper, the growth looked impressive. But without a proper
                        capital and solvency review, the business was operating on thinner margins than the
                        directors realised — and the liability exposure from the expanded fleet had not been
                        adequately assessed.
                      </p>
                      <p>
                        It took one major incident and an audit to reveal the gap. The
                        recovery cost far more than a structured advisory engagement would have.
                      </p>
                      <p className="rpg-case__footer">This isn't an unusual story. It's a common one.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Section 5 ── */}
            <section className={`rpg-section ${vis('section-5')}`} id="section-5">
              <div className="rpg-section__number" aria-hidden="true">
                <span>05</span>
                <div className="rpg-section__number-line" />
              </div>
              <div className="rpg-section__body">
                <h2 className="rpg-h2">
                  <span className="rpg-h2__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
                    </svg>
                  </span>
                  What This Means<br/><em>for You</em>
                </h2>
                <p>
                  The businesses that will lead in the next decade aren't necessarily the ones with the
                  biggest budgets — they're the ones making the clearest decisions. Risk management, done
                  well, isn't about being cautious. It's about being precise. It's about knowing exactly
                  where you stand so you can move forward with confidence.
                </p>

                <div className="rpg-closing-statement">
                  <div className="rpg-closing-statement__bg" aria-hidden="true">
                    <svg viewBox="0 0 400 200" fill="none" preserveAspectRatio="xMidYMid slice">
                      <circle cx="350" cy="50" r="100" fill="#14b8a6" fillOpacity="0.03"/>
                      <circle cx="50" cy="150" r="80" fill="#0d9488" fillOpacity="0.02"/>
                    </svg>
                  </div>
                  <p>The question isn't whether your business has risk.</p>
                  <p className="rpg-closing-statement__accent">Every business does.</p>
                  <p>The question is whether <strong>you understand yours.</strong></p>
                </div>
              </div>
            </section>

          </article>
        </div>

        {/* ── CTA ── */}
        <section className={`rpg-cta ${vis('cta')}`} id="cta">
          <div className="rpg-cta__bg" aria-hidden="true">
            <svg viewBox="0 0 1200 400" fill="none" preserveAspectRatio="xMidYMid slice">
              <defs>
                <radialGradient id="ctaGrad" cx="50%" cy="50%" r="60%">
                  <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.15"/>
                  <stop offset="100%" stopColor="#0d9488" stopOpacity="0"/>
                </radialGradient>
                <pattern id="ctaGrid" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#14b8a6" strokeWidth="0.5" strokeOpacity="0.06"/>
                </pattern>
              </defs>
              <rect width="1200" height="400" fill="url(#ctaGrad)"/>
              <rect width="1200" height="400" fill="url(#ctaGrid)"/>

              {/* Floating orbs */}
              {[...Array(5)].map((_, i) => (
                <circle 
                  key={i} 
                  cx={150 + i * 220} 
                  cy={i % 2 === 0 ? 80 : 320} 
                  r={30 + i * 15}
                  fill="none" 
                  stroke="#0d9488" 
                  strokeWidth="1" 
                  strokeOpacity="0.06" 
                  strokeDasharray="8 4"
                />
              ))}

              <path d="M0 200 Q300 150 600 200 T1200 180" stroke="#14b8a6" strokeWidth="1.5" fill="none" strokeOpacity="0.08"/>
            </svg>
          </div>

          <div className="rpg-cta__inner">
            <div className="rpg-cta__eyebrow-wrap">
              <span className="rpg-cta__eyebrow-line" aria-hidden="true"/>
              <span className="rpg-cta__eyebrow">Ready to start?</span>
              <span className="rpg-cta__eyebrow-line" aria-hidden="true"/>
            </div>
            <h3 className="rpg-cta__title">Take the First Step</h3>
            <p className="rpg-cta__body">
              If you've never had an independent review of your business's risk and financial
              position, now is a good time to start. Our team brings deep expertise in actuarial
              science, capital advisory, and financial modelling — translated into practical guidance
              that works for businesses at every stage.
            </p>
            <Link to="/contact" className="rpg-cta__btn">
              <span>Get in touch with Actyra</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </section>

        {/* ── Back nav ── */}
        <div className="rpg-bottom-nav">
          <div className="rpg-bottom-nav__line" aria-hidden="true"/>
          <Link to="/blogs" className="rpg-back rpg-back--large">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Back to all blogs</span>
          </Link>
        </div>

      </main>
    </>
  );
}